import React, {ChangeEvent, useState} from 'react';
import moment, {Moment} from "moment";
import {AccountDetailsType,} from 'src/Assets/models/StandaloneAccount';
import {DISPLAY_DATE_FORMAT, ISO8601_DATE_FORMAT} from "../../constants/common";
import {booleanToRadioButton, radioButtonToBoolean} from '../formHelpers';
import {RadioYesNoOptional} from '../models/Common';
import {
    DatePicker,
    Dropdown,
    DropdownItem,
    Input,
    RadioGroup,
    RedAsterisk,
    RequiredFieldsSubheader,
    UnderlinedHeader
} from "../../components";
import DropdownGroup from "@nt/dds-react/core/Dropdown/DropdownGroup"
import {TaxStatusOptions} from "../models/TaxDetails";
import {InstitutionOptions} from '../models/Institutions';

const taxStatusOptions = Object.freeze(["Taxable", "Deferred", "Exempt"]);
const taxStatusDropdownItems = taxStatusOptions.map(taxOption => (<DropdownItem key={taxOption}
                                                                                itemText={taxOption}
                                                                                value={taxOption}/>));
InstitutionOptions.sort((a, b) => {
    if (a.label === 'None' || a.label === 'Other') return 1;
    if (b.label === 'None' || b.label === 'Other') return -1;
    return a.label.localeCompare(b.label);
})
export const isFutureDate = (date: Moment) => date.isAfter(moment().endOf('day'));

type AccountDetailsProps = {
    accountDetails: AccountDetailsType,
    handleUpdateAccountDetails: (e: ChangeEvent<HTMLInputElement>) => void,
    updateAccountDetails: (accountDetails: AccountDetailsType) => void,
    handleTaxStatusChange: (value: TaxStatusOptions) => void,
    isRequiredFieldsBannerShown: boolean,
    isProfileWithProposalsOrArchived?: boolean
}

const AccountDetails: React.FC<AccountDetailsProps> = ({
                                                           accountDetails,
                                                           handleUpdateAccountDetails,
                                                           updateAccountDetails,
                                                           handleTaxStatusChange,
                                                           isRequiredFieldsBannerShown,
                                                           isProfileWithProposalsOrArchived = false,
                                                       }) => {
    const [isNameInlineErrorShown, setIsNameInlineErrorShown] = useState(false);
    const [isAsOfDateEmptyOnBlur, setIsAsOfDateEmptyOnBlur] = useState(false);
    const [isInstitutionInlineErrorShown, setIsInstitutionInlineErrorShown] = useState(false);
    const [isOtherInstitutionInlineErrorShown, setIsOtherInstitutionInlineErrorShown] = useState(false);
    const [InstitutionHeight, setInstitutionHeight] = useState<number | string>(300)
    const [institutionSearchInput, setInstitutionSearchInput] = useState('');

    const isAsOfDateInlineErrorShown = () => {
        const isAsOfDateEmptyOnSave = isRequiredFieldsBannerShown && !accountDetails.asOfDate?.trim();
        return isAsOfDateEmptyOnBlur || isAsOfDateEmptyOnSave;
    };

    const savedInstitutions: string | undefined = localStorage.getItem('recentInstitution')?.trim()
    const institutionArray: [string] = savedInstitutions ? JSON.parse(savedInstitutions) : []
    const recentInstitutionItems = institutionArray.filter(inst => InstitutionOptions.find(item => item.value === inst)).map(option => {
        const firm = InstitutionOptions.find(item => item.value === option);
        return firm ? (<DropdownItem key={firm.value} itemText={firm.label} value={firm.value}/>) : null;
    });

    const getSortedInstitutionOptions = (userInput: string) => {
        const filtered = InstitutionOptions.filter(option => !institutionArray.includes(option.value));
        if (!userInput || userInput.length < 1) return filtered;

        const input = userInput.trim().toLowerCase();

        function findIndexOfInput(label: string) {
            const words = label.split(/\s+/);
            for (let i = 0; i < words.length; i++) {
                if (words[i].toLowerCase().startsWith(input)) {
                    return { index: i, word: words[i].toLowerCase() };
                }
            }
            return { index: Number.POSITIVE_INFINITY, word: "" };
        }

        return filtered.slice().sort((a, b) => {
            const aInfo = findIndexOfInput(a.label);
            const bInfo = findIndexOfInput(b.label);

            if (aInfo.index !== bInfo.index) return aInfo.index - bInfo.index;
            if (aInfo.index !== Number.POSITIVE_INFINITY && bInfo.index !== Number.POSITIVE_INFINITY) {
                if (aInfo.word !== bInfo.word) return aInfo.word.localeCompare(bInfo.word);
            }
            return a.label.localeCompare(b.label);
        });
    };

    const getDefaultText = () => {
        if (!InstitutionOptions.some(firm => firm.value === accountDetails.institution) && accountDetails.institution) {
            return accountDetails.institution
        } else {
            return "Select or search..."
        }
    }
    const getInstitutionError = () => {
        if (isInstitutionInlineErrorShown || accountDetails.institution === null) {
            return "Institution is required."
        } else if (!InstitutionOptions.some(firm => firm.value === accountDetails.institution) && accountDetails.institution !== '' && accountDetails.institution?.slice(0, 7) !== 'Other: ') {
            return "Institution name is invalid. Please select from the dropdown"
        } else {
            return undefined
        }
    }

    const getOtherInstitutionError = () => {
        const restrictedChars = /[,\\|\[\]?.()_{}=@!$%#^+~`;'"<>]/
        const otherInstitution: string = accountDetails.otherInstitution ? accountDetails.otherInstitution : ''
        if (accountDetails.institution === 'Other' && isOtherInstitutionInlineErrorShown) {
            return "Other Institution is required, please type name of institution."
        } else if (restrictedChars.test(otherInstitution)) {
            return 'Only these special characters are allowed: & - /'
        }
    }
    return (
        <section className="asset-details-section">
            <UnderlinedHeader
                className="asset-details-section-header"
                primaryText="Account Details"
                rightAlignedContent={<RequiredFieldsSubheader/>}
            />
            <div className="layout-data-entry-form__field">
                <label className="nameLabel" id="nameFieldInput-label">
                    <b>Name<RedAsterisk/></b>
                </label>
                <Input
                    className="nameFieldInput"
                    name="name"
                    error={isNameInlineErrorShown ? "Name is required." : undefined}
                    aria-labelledby="nameFieldInput-label"
                    aria-label="name"
                    id="nameFieldInput"
                    onChange={handleUpdateAccountDetails}
                    onBlur={() => setIsNameInlineErrorShown(!accountDetails.name.trim())}
                    removeMarginTop
                    size="medium"
                    type="text"
                    maxLength={100}
                    value={accountDetails.name}
                    readOnly={isProfileWithProposalsOrArchived}
                />
            </div>
            <div className="layout-data-entry-form__field">
                <label className="asOfDateLabel">
                    <b>As of Date<RedAsterisk/></b>
                </label>
                <div style={{position: "relative"}}>
                    <DatePicker
                        className="asOfDateInput"
                        id="asOfDateInput"
                        displayFormat={DISPLAY_DATE_FORMAT}
                        error={isAsOfDateInlineErrorShown() ? "As of Date is required." : ''}
                        hideKeyboardShortcutsPanel
                        isOutsideRange={isFutureDate}
                        date={accountDetails.asOfDate ? moment(accountDetails.asOfDate) : undefined}
                        onDateChange={
                            (date: Moment) => {
                                const asOfDate = date?.utc().format(ISO8601_DATE_FORMAT);
                                updateAccountDetails({
                                    ...accountDetails,
                                    asOfDate,
                                });
                                setIsAsOfDateEmptyOnBlur(!asOfDate?.trim());
                            }
                        }
                        disabled={isProfileWithProposalsOrArchived}
                    />
                </div>
            </div>
            <div className="layout-data-entry-form__field">
                <label className="accountNumberLabel" id="accountNumberInput-label">
                    <b>Account Number</b>
                </label>
                <Input
                    className="accountNumberInput"
                    name="accountNumber"
                    aria-labelledby="accountNumberInput-label"
                    id="accountNumberInput"
                    onChange={handleUpdateAccountDetails}
                    removeMarginTop
                    size="medium"
                    type="text"
                    maxLength={100}
                    value={accountDetails.accountNumber}
                    readOnly={isProfileWithProposalsOrArchived}
                />
            </div>
            <div className="layout-data-entry-form__field">
                <label className="entityTypeLabel" id="entityTypeInput-label">
                    <b>Entity Type</b>
                </label>
                <Input
                    className="entityTypeInput"
                    name="entityType"
                    aria-labelledby="entityTypeInput-label"
                    id="entityTypeInput"
                    onChange={handleUpdateAccountDetails}
                    removeMarginTop
                    size="medium"
                    type="text"
                    maxLength={100}
                    value={accountDetails.entityType}
                    readOnly={isProfileWithProposalsOrArchived}
                />
            </div>
            <div className="layout-data-entry-form__field">
                <>
                    <label className="institutionLabel" id="institutionInput-label">
                        <b>Institution<RedAsterisk/></b>
                    </label>
                    <Dropdown
                        defaultText={getDefaultText()}
                        className='institutionInput'
                        disabled={isProfileWithProposalsOrArchived}
                        dropUp={false}
                        error={getInstitutionError()}
                        iconNameClose="arrow_drop_up"
                        iconNameOpen="arrow_drop_down"
                        id="institutionInput"
                        name="institution"
                        nativeOnMobile={false}
                        customSearchFilter={
                            (option, userInput) => {
                                setInstitutionSearchInput(userInput);
                                if (userInput.length >= 1) {
                                    setInstitutionHeight('auto');
                                    if (option === 'Other' || option === 'None') return true;
                                    const regex = new RegExp(`(^| )${userInput}`, 'i');
                                    return regex.test(option);
                                } else {
                                    setInstitutionHeight(300);
                                    return option;
                                }
                            }
                        }
                        onChange={(e) => {
                            if (e.value) {
                                updateAccountDetails({
                                    ...accountDetails,
                                    institution: e.value,
                                });
                            }
                        }}
                        panelHeight={InstitutionHeight}
                        searchable
                        onBlur={() => setIsInstitutionInlineErrorShown(!accountDetails.institution?.trim())}
                        open={false}
                        buttonSize="medium"
                        value={accountDetails.institution}
                        virtualScroll={false}
                    >
                        <DropdownGroup groupName='MOST RECENT'>
                            {recentInstitutionItems}
                        </DropdownGroup>
                        {getSortedInstitutionOptions(institutionSearchInput).map(firm => (
                            firm?.value === 'None' ?
                                <DropdownItem key={firm.value} itemText={firm.label} value={firm.value}
                                              className='border-top bordercolor-grey padding-md'/> :
                                <DropdownItem key={firm?.value} itemText={firm?.label} value={firm?.value}/>
                        ))}
                    </Dropdown>
                </>
            </div>
            {accountDetails.institution === 'Other' &&
                <div className="layout-data-entry-form__field">
                    <label className='otherInstitutionLabel' id="otherInstitutionInput-label"><b>Other
                        Institution</b><RedAsterisk/></label>
                    <Input
                        autoFocus={true}
                        className="institutionInput"
                        name="otherInstitution"
                        aria-labelledby="otherInstitutionInput-label"
                        error={getOtherInstitutionError()}
                        id="otherInstitutionInput"
                        onChange={handleUpdateAccountDetails}
                        onBlur={() => setIsOtherInstitutionInlineErrorShown(!accountDetails.otherInstitution?.trim())}
                        removeMarginTop
                        size="medium"
                        type="text"
                        value={accountDetails.otherInstitution}
                        maxLength={100}
                        readOnly={isProfileWithProposalsOrArchived}
                    />
                </div>
            }

            <div className="layout-data-entry-form__field">
                <label id="taxStatus">
                    <b>Tax Status</b>
                </label>
                <Dropdown
                    className="taxStatusField"
                    name="taxStatus"
                    id="taxStatusInput"
                    aria-labelledby="taxStatus"
                    buttonSize="medium"
                    onChange={(data: any) => handleTaxStatusChange(data?.value as TaxStatusOptions)}
                    value={accountDetails.taxStatus}
                    disabled={isProfileWithProposalsOrArchived}
                >
                    {taxStatusDropdownItems}
                </Dropdown>
            </div>
            {(accountDetails.taxStatus === "Deferred" || accountDetails.taxStatus === "Exempt") &&
                <RadioGroup
                    id="doesPermitBeneficiary"
                    name="doesPermitBeneficiary"
                    label="Does this account permit a beneficiary?"
                    layout="horizontal"
                    values={["Yes", "No"]}
                    disabled={isProfileWithProposalsOrArchived}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        const {value} = e.target;
                        updateAccountDetails({
                            ...accountDetails,
                            doesPermitBeneficiary: radioButtonToBoolean(value as RadioYesNoOptional),
                        })
                    }}
                    selected={booleanToRadioButton(accountDetails.doesPermitBeneficiary)}
                />
            }
        </section>
    )
};

export default AccountDetails;
